import { CheckOutlined } from "@ant-design/icons";
import {
  Avatar,
  Button,
  Input,
  InputNumber,
  List,
  Select,
  Typography,
} from "antd";
import { useEffect, useState } from "react";
import { useAppSelector } from "../../../../../../app/store";
import SearchIcon from "../../../../../../assets/icons/SearchIcon";
import ActionModal from "../../../../../../components/ui/ActionModal";
import CustomModal from "../../../../../../components/ui/CustomModal";
import {
  useGetAllDepartmentQuery,
  useGetAllEmployeesQuery,
} from "../../../../../../features/superAdmin/employeeService/employeeManagementService";
import { useForwardTicketToEmployeeMutation } from "../../../../../../features/superAdmin/ticketService/ticket/ticket.api";
import useDebounce from "../../../../../../hooks/useDebounce";
import useError from "../../../../../../hooks/useError";
import useModal from "../../../../../../hooks/useModal";
import { ModalComponentProps } from "../../../../../../types";

type ForwardTicketModalProps = ModalComponentProps & {
  handleParentClose?: () => void;
  id?: number | string;
  raiserId?: number;
  userData?: {
    id: number;
    name: string;
    avatar: null;
  };
};

export default function ForwardOpenTicketModal({
  handleClose,
  handleParentClose,
  id,
  raiserId,
  userData,
  ...rest
}: ForwardTicketModalProps) {
  const {
    open: openViewModal,
    handleOpen: handleViewOpen,
    handleClose: handleViewClose,
  } = useModal<undefined, "VIEW">();

  const { keyword, handleInputChange } = useDebounce();
  const [selectedDeptId, setSelectedDeptId] = useState<number>();
  const [selectedUserId, setSelectedUserId] = useState<number>();
  const [hour, setHour] = useState<number>();
  const [minute, setMinute] = useState<number>();
  const [timeError, setTimeError] = useState<string | null>(null);
  const userId = useAppSelector((state) => state.auth.user?.id);
  const { data: employeesResponse } = useGetAllEmployeesQuery({
    params: {
      department: selectedDeptId || undefined,
      search: keyword,
      page: 1,
      limit: 10000000,
    },
  });

  const employeeOptions = userData?.id
    ? [userData]
    : employeesResponse?.data?.employees
        ?.map((em) => ({
          id: em?.id,
          name: em?.officialInfo?.name,
          avatar: em?.personalInfo?.pro_pic,
        }))
        ?.filter((e) => e.id !== raiserId && e.id !== userId);

  const {
    isError: allDepartmentIsError,
    error: allDepartmentError,
    data: allDepartmentData,
    isLoading: allDepartmentIsLoading,
  } = useGetAllDepartmentQuery({
    params: {
      page: 1,
      limit: 100000000,
      status: "active",
    },
  });

  const [
    forwardOpenTicket,
    {
      isError: forwardOpenTicketError,
      error: forwardOpenTicketErrorData,
      isSuccess: updateForwardTicketIsSuccess,
      isLoading: updateForwardTicketIsLoading,
    },
  ] = useForwardTicketToEmployeeMutation();

  const handleForwardTicket = () => {
    const h = Number(hour || 0);
    const m = Number(minute || 0);

    if (
      isNaN(h) ||
      isNaN(m) ||
      h < 0 ||
      m < 0 ||
      m >= 60 ||
      (h === 0 && m === 0)
    ) {
      setTimeError("Please enter a valid time.");
      return;
    }

    if (!selectedUserId) return;

    const employee = employeesResponse?.data?.employees?.find(
      (em) => em.id === selectedUserId,
    );

    const totalMinutes = h * 60 + m;

    const forwardInfo = {
      employee_id: selectedUserId,
      employee_name: employee?.officialInfo?.name,
      dept_id: employee?.officialInfo?.department?.id,
      dept_name: employee?.officialInfo?.department?.name,
      visibility_duration_minutes: totalMinutes,
    };

    forwardOpenTicket({ data: forwardInfo, id });
  };

  useError(
    allDepartmentIsError,
    allDepartmentError,
    "Get all department list fail",
  );

  useError(
    forwardOpenTicketError,
    forwardOpenTicketErrorData,
    "Forward ticket fail",
  );

  useEffect(() => {
    if (updateForwardTicketIsSuccess) {
      handleViewOpen({ type: "VIEW" });
      setSelectedUserId(undefined);
      setMinute(undefined);
      setHour(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateForwardTicketIsSuccess]);

  return (
    <>
      {openViewModal.type === "VIEW" && (
        <ActionModal
          title={`Ticket Forward Successfully`}
          subTitle={`The ticket has been forwarded successfully.`}
          open={openViewModal?.open}
          handleClose={() => {
            handleClose();
            handleViewClose();
          }}
          handleAction={() => {
            handleClose();
            handleViewClose();
          }}
          cancelButton={false}
        />
      )}

      <CustomModal
        title="Select Department"
        {...rest}
        handleClose={handleClose}
        width={400}
        footer={null}
      >
        <div>
          <Input
            size="large"
            prefix={<SearchIcon />}
            placeholder="Search"
            onChange={(e) => handleInputChange(e)}
            // {keyword}
            allowClear
          />
          <div className="mt-3 grid grid-cols-2 gap-3">
            <div>
              <Select
                size="large"
                disabled={allDepartmentIsLoading}
                showSearch
                placeholder="Department"
                optionFilterProp="label"
                options={allDepartmentData?.data.map((dep: any) => ({
                  label: dep.name,
                  value: dep.id,
                }))}
                onChange={(value) => setSelectedDeptId(value)}
                value={selectedDeptId}
                allowClear
                className="w-full"
              />
            </div>
            <div>
              <div className="flex items-center gap-1">
                <InputNumber
                  size="large"
                  placeholder="HH"
                  min={0}
                  max={23}
                  className="w-full"
                  value={hour}
                  onChange={(value) => {
                    setHour(value ?? 0);
                    setTimeError(null);
                  }}
                />
                <span className="text-gray-400">:</span>
                <InputNumber
                  size="large"
                  placeholder="MM"
                  min={0}
                  max={59}
                  className="w-full"
                  value={minute}
                  onChange={(value) => {
                    setMinute(value ?? 0);
                    setTimeError(null);
                  }}
                />
              </div>
              {timeError && (
                <div className="text-red-500 text-xs mt-1">{timeError}</div>
              )}
            </div>
          </div>

          <div className="overflow-y-auto max-h-[300px] my-4">
            <List
              dataSource={employeeOptions || []}
              renderItem={(item) => (
                <List.Item
                  style={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    padding: "8px 0",
                  }}
                  onClick={() => setSelectedUserId(item.id)}
                >
                  <Avatar src={item.avatar} />
                  <Typography.Text style={{ marginLeft: 12, flex: 1 }}>
                    {item.name}
                  </Typography.Text>
                  {selectedUserId === item.id && (
                    <CheckOutlined style={{ color: "#1677ff", fontSize: 16 }} />
                  )}
                </List.Item>
              )}
            />
          </div>
        </div>
        <div className="py-2 flex justify-between">
          <Button className="button-bordered-light h-10" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            loading={updateForwardTicketIsLoading}
            onClick={handleForwardTicket}
            disabled={!selectedUserId}
            color="primary"
            className="button-primary-light h-10"
          >
            Forward
          </Button>
        </div>
      </CustomModal>
    </>
  );
}
